import { animalPieceTypes, directions } from "../const";
import { Move } from "../Move";
import {
  IPiece,
  PieceMovement,
  Position,
  IMove,
  MoveType,
  ISquare,
  Effect,
} from "../types";
import { BaseMovement } from "./BaseMovement";

export class VirusMovement extends BaseMovement implements PieceMovement {
  getValidMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    isLightTurn?: boolean,
    gameStateManager?: any
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    // Check if we're in a bonus turn (infection chain)
    // We determine this by checking if we're on the same square as another piece
    // (i.e., the piece we just infected)
    const currentSquare = boardState[position.row][position.col];
    const isOnInfectedPiece = currentSquare.Pieces.length > 1;
    // When virus is stacked, departing will capture the animal beneath.
    // Used to exclude that position from future target checks (it won't exist post-move).
    const leavingPosition = isOnInfectedPiece ? position : undefined;

    // OFFICIAL RULES (3.14a, 3.14b):
    // - Two squares ONLY in straight line (orthogonal or diagonal)
    // - Unless impeded by edge, then one square
    // - Can ONLY move to animal pieces (friendly or enemy), NOT empty squares
    // - When stacked (active/infecting), can leave to capture below

    for (const direction of directions) {
      // Try 2 squares away first (normal movement)
      const twoSquaresAway = {
        col: position.col + direction[0] * 2,
        row: position.row + direction[1] * 2,
      };

      if (this.onBoard(boardState, twoSquaresAway)) {
        // Two squares is on board - this is the preferred move
        const targetSquare = boardState[twoSquaresAway.row][twoSquaresAway.col];
        const targetPiece = targetSquare.Pieces.find(p => animalPieceTypes.includes(p.type));

        // RULE 3.14b: Can ONLY move to animal pieces
        if (targetPiece && animalPieceTypes.includes(targetPiece.type)) {
          const move = new Move(
            piece,
            twoSquaresAway,
            MoveType.infect,
            targetPiece
          );
          // RULE 3.14g: Bonus turn for infection - ONLY if there will be valid moves from landing position
          // This prevents granting a useless bonus turn that immediately ends
          if (this.hasInfectionTargetsFromPosition(twoSquaresAway, boardState, leavingPosition)) {
            move.addSideEffect(twoSquaresAway, [piece], Effect.BonusTurn());
          }
          possibleMoves.push(move);
        }
      } else {
        // Two squares is OFF board - check if one square is available (edge rule)
        const oneSquareAway = {
          col: position.col + direction[0],
          row: position.row + direction[1],
        };

        if (this.onBoard(boardState, oneSquareAway)) {
          const targetSquare = boardState[oneSquareAway.row][oneSquareAway.col];
          const targetPiece = targetSquare.Pieces.find(p => animalPieceTypes.includes(p.type));

          // RULE 3.14b: Can ONLY move to animal pieces
          if (targetPiece && animalPieceTypes.includes(targetPiece.type)) {
            const move = new Move(
              piece,
              oneSquareAway,
              MoveType.infect,
              targetPiece
            );
            // RULE 3.14g: Bonus turn for infection - ONLY if there will be valid moves from landing position
            if (this.hasInfectionTargetsFromPosition(oneSquareAway, boardState, leavingPosition)) {
              move.addSideEffect(oneSquareAway, [piece], Effect.BonusTurn());
            }
            possibleMoves.push(move);
          }
        }
      }
    }

    // RULE 3.14g: "MAY make as many movements... without ending its turn"
    // When stacked (bonus turn active), player can CHOOSE to end the turn instead of continuing
    // This prevents forced captures and gives the player control over the infection chain
    // IMPORTANT: Only available during bonus turns (when stacked), NOT on first move!
    // The Virus can only end turn AFTER making at least one infection move (bonus turn).
    const isInBonusTurn = gameStateManager?.getGameState?.()?.activeBonusTurnPieceId === piece.id;
    if (isOnInfectedPiece && isInBonusTurn) {
      const endTurnMove = new Move(
        piece,
        position, // Stay in same position
        MoveType.endTurn,
        null
      );
      possibleMoves.push(endTurnMove);
    }

    return possibleMoves;
  }

  /**
   * Checks if there will be any valid infection targets from a given position.
   * Used to determine whether to grant a bonus turn after an infection.
   * This prevents granting useless bonus turns that immediately end.
   */
  private hasInfectionTargetsFromPosition(
    fromPosition: Position,
    boardState: ISquare[][],
    leavingPosition?: Position
  ): boolean {
    for (const direction of directions) {
      // Check 2 squares away (normal movement)
      const twoSquaresAway = {
        col: fromPosition.col + direction[0] * 2,
        row: fromPosition.row + direction[1] * 2,
      };

      if (this.onBoard(boardState, twoSquaresAway)) {
        // Skip the position the virus is departing from — the animal there will be captured
        if (leavingPosition && twoSquaresAway.row === leavingPosition.row && twoSquaresAway.col === leavingPosition.col) {
          continue;
        }
        const targetSquare = boardState[twoSquaresAway.row][twoSquaresAway.col];
        const targetPiece = targetSquare.Pieces.find(p => animalPieceTypes.includes(p.type));
        if (targetPiece) {
          return true; // Found a valid infection target
        }
      } else {
        // Check 1 square away (edge rule)
        const oneSquareAway = {
          col: fromPosition.col + direction[0],
          row: fromPosition.row + direction[1],
        };

        if (this.onBoard(boardState, oneSquareAway)) {
          // Skip the position the virus is departing from
          if (leavingPosition && oneSquareAway.row === leavingPosition.row && oneSquareAway.col === leavingPosition.col) {
            continue;
          }
          const targetSquare = boardState[oneSquareAway.row][oneSquareAway.col];
          const targetPiece = targetSquare.Pieces.find(p => animalPieceTypes.includes(p.type));
          if (targetPiece) {
            return true; // Found a valid infection target
          }
        }
      }
    }

    return false; // No valid infection targets from this position
  }
}
